import { AuditStatusEnum } from "@/pages/relayYardControl/AccuracyView/constants/filters";
import { JobData, LicensePlateIdentifier } from "./jobs";
import type { WebSocketMessageStatus } from "@/services/websocket/types";

// Alert type enum
export type AlertType =
  | "LOCATION"
  | "CONTENT"
  | "CONTENT_TAG"
  | "VR"
  | "MISSING_CHECK_IN"
  | "SPEEDING_ALERT"
  | "PROXIMITY_ALERT"
  | "UNKNOWN_VEHICLE";

export const ALERT_TYPES = {
  LOCATION: "LOCATION",
  CONTENT: "CONTENT",
  CONTENT_TAG: "CONTENT_TAG",
  VR: "VR",
  MISSING_CHECK_IN: "MISSING_CHECK_IN",
  SPEEDING_ALERT: "SPEEDING_ALERT",
  PROXIMITY_ALERT: "PROXIMITY_ALERT",
  UNKNOWN_VEHICLE: "UNKNOWN_VEHICLE",
} as const;

export const FALLBACK_TITLES = {
  MISSING_CHECK_IN: "Displaced Asset",
  SPEEDING_ALERT: "Speeding",
  PROXIMITY_ALERT: "Safety Bubble",
  UNKNOWN_VEHICLE: "Unverified Asset",
} as const;

// VR State for VR alerts
export interface VrState {
  vrId: string;
  shipperAccount: string;
  destinationScheduledArrivalTime: number;
  origin: string;
  destination: string;
  originScheduledDepartureTime: number;
  referenceId: string;
  originScheduledArrivalTime: number;
}

// SWA Alert Sub-Types for MISSING_CHECK_IN alerts
export type SwaAlertSubType = "TRAILER_LOST_RELAY_CHECK_IN" | "TRAILER_LOST_PHYSICAL_ARRIVAL";

export const SWA_ALERT_SUB_TYPES = {
  TRAILER_LOST_RELAY_CHECK_IN: "TRAILER_LOST_RELAY_CHECK_IN",
  TRAILER_LOST_PHYSICAL_ARRIVAL: "TRAILER_LOST_PHYSICAL_ARRIVAL",
} as const;

// Missing Check-In State for MISSING_CHECK_IN alerts
export interface MissingCheckInState {
  equipmentDetails: AlertEquipment;
  plannedCheckInTime?: number;
  buildingCode?: string;
  swaAlertSubType?: SwaAlertSubType;
  vrId?: string;
  ymsCheckInTime?: number | null;
}

// Resolution type for alerts resolved by an upstream/source system.
// Values match the ResolutionType enum in YardJobManagerServiceModel (Yard-5719).
export type ResolutionType =
  | "SOURCE_SYSTEM_RESOLVED"
  | "JOB_COMPLETED"
  | "JOB_EXPIRED"
  | "USER_COMPLETED"
  | "USER_DISMISSED";

export const RESOLUTION_TYPES = {
  SOURCE_SYSTEM_RESOLVED: "SOURCE_SYSTEM_RESOLVED",
  JOB_COMPLETED: "JOB_COMPLETED",
  JOB_EXPIRED: "JOB_EXPIRED",
  USER_COMPLETED: "USER_COMPLETED",
  USER_DISMISSED: "USER_DISMISSED",
} as const;

// Equipment related types
export interface AlertEquipment {
  equipmentId?: string;
  equipmentNumber?: string;
  type?: string;
  ownerCode?: string;
  licensePlateIdentifier?: LicensePlateIdentifier;
  visitId?: string;
}

export interface AlertObject {
  type?: string; // "VEHICLE", "LARGE_VEHICLE", "PERSON", etc
  vehicle?: AlertEquipment | null;
}

// Safety state for SPEEDING_ALERT / PROXIMITY_ALERT alerts
export interface SafetyAlertState {
  driverLogin?: string;
  startTime?: number;
  endTime?: number;
  latitude?: number;
  longitude?: number;
  offendingObject?: AlertObject | null;
  defendingObject?: AlertObject | null;
}

// Location state related types
export interface EquipmentLocationState {
  locationLabel?: string;
  confidence?: number;
}

// Content state related types
export interface EquipmentContentState {
  isSealed?: boolean;
  sealCount?: number;
  confidence?: number;
}

export interface ContentTagExpectedState {
  loadType?: string;
  loadSubType?: string;
}

export interface EquipmentState {
  locationState?: EquipmentLocationState;
  contentState?: EquipmentContentState;
  detectionSource?: string;
  detectionTimestamp?: string;
  source?: string;
  lastUpdatedTimestamp?: string;
}

// Alert data structure matching API response
export interface AlertData {
  alertId: string;
  yardAlertStatus: AuditStatusEnum;
  alertType: AlertType;
  timestamp: number;
  yardId: string;
  equipmentDetails?: AlertEquipment;
  expectedState?: ContentTagExpectedState;
  physicalState?: EquipmentState;
  virtualState?: EquipmentState;
  vrState?: VrState;
  missingCheckInState?: MissingCheckInState;
  safetyState?: SafetyAlertState;
  status?: string;
  version?: number;
  sequenceId?: number;
  datapathViewVersion?: string;
}

// Job metadata types
export interface GateLane {
  laneIdentifier: string;
  purpose: string;
}

export interface Gate {
  gateIdentifier: string;
}

export interface GateDetails {
  lane: GateLane;
  gate: Gate;
}

export interface JobLocation {
  identifier: {
    id: string | null;
    code: string;
  };
  code: string;
  type: string | null;
  zones: string[] | null;
  zone: string | null;
  multiCapacity: boolean | null;
}

// Base Alert interface matching API response format
export interface Alert<T extends string = string> {
  yardAlertId: string;
  data: {
    alertVersion?: number;
    alert: AlertData;
    job?: JobData<T>;
    location?: {
      locationCode: string;
    };
    lastUpdatedTimestamp: string;
    createdTimestamp: string;
    ttl?: number;
    status: AuditStatusEnum;
    yardAlertId: string;
    resolutionType?: ResolutionType | null;
  };
  yardId: string;
}

// YardAlert interface for internal use
export interface YardAlert<T extends string = string> extends Alert<T> {
  locationCode?: string;
  datapathViewVersion?: string;
}

// Type alias for MISSING_CHECK_IN alerts with proper JobOutputData typing
export type MissingCheckInAlert = YardAlert<"MISSING_CHECK_IN">;

export interface YardAlertsResponse {
  alerts: YardAlert[] | undefined;
}

export interface WebsocketAlertMessage {
  record: YardAlert;
  metadata: {
    status: WebSocketMessageStatus;
  };
  sortType?: string;
  dataType?: string;
  [key: string]: any;
}
